import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api } from '../lib/api.js';
import { photoSrc } from '../lib/PhotoPicker.js';

/**
 * Who has a photograph, for every admin list that shows an employee.
 *
 * The owner asked for the face wherever an employee appears — attendance, weekly off, flags, pay
 * slips, reports. Those lists come from a dozen routes that know nothing about photographs; one
 * map of id → time, loaded once, lets each of them ask for the picture by id without any of
 * those routes changing. A list that renders before the map arrives shows initials, then the
 * face — the same fallback the Avatar already uses for a photo that fails to load.
 */
type Photos = {
  /** The URL of this employee's photo, or null when they have none (initials are shown). */
  photoOf: (userId: string) => string | null;
  /** Ask again — after a photo is added, changed or removed on this device. */
  refresh: () => void;
};

const PhotoContext = createContext<Photos>({ photoOf: () => null, refresh: () => {} });

export function PhotosProvider({ children }: { children: ReactNode }) {
  const [times, setTimes] = useState<Record<string, string>>({});
  const refresh = useCallback(() => {
    api<{ photos: Record<string, string> }>('/admin/worker-photos')
      .then((r) => setTimes(r.photos))
      // A missing face is initials, which is what the page showed before this existed.
      .catch(() => {});
  }, []);
  useEffect(refresh, [refresh]);
  const photoOf = useCallback(
    (userId: string) => photoSrc(`/admin/workers/${userId}/photo`, times[userId]),
    [times],
  );
  return <PhotoContext.Provider value={{ photoOf, refresh }}>{children}</PhotoContext.Provider>;
}

export const usePhotos = () => useContext(PhotoContext);
